import { Controller, useFormContext } from 'react-hook-form';
import Select from 'react-select';
import { inputStyles } from '~/components/Vendors/styles';
import { toUSD } from '~/utils';
import UploadDocument from './UploadDocuments';
import { useEffect, useState, useMemo } from 'react';
import {
    FundRequestExpenseTypes,
    fundRequestExpenseTypesList,
} from '~/enums/fundRequest';
import { useFundRequestContext } from '~/context/FundRequestProvider';
import { DollarInput } from '~/common/form/Inputs';
import { apiUrl, authHeaders } from '~/services/auth';
import {
    fetchTravelSchema,
    calculateTravelTotal,
    ratesForTravelDate,
    TravelSchema,
} from '@shared/expenses/travel';
import { SelectOption } from '~/types/globals';

interface TravelExpenseFormProps {
    onDocumentClick?: (slideIndex: number) => void;
    selectedDocumentIndex?: number;
}

const TravelExpenseForm = ({
    onDocumentClick,
    selectedDocumentIndex,
}: TravelExpenseFormProps) => {
    const { formKey, annualRates, glCodes } = useFundRequestContext();
    const {
        control,
        setValue,
        watch,
        getValues,
        register,
        formState: { errors },
    } = useFormContext();
    const [totalAmount, setTotalAmount] = useState<number>(0);

    const [travelSchema, setTravelSchema] = useState<TravelSchema | null>(null);
    // Watch relevant numeric fields explicitly so total recomputes on every change
    const [
        dateOfTravel,
        milesDriven,
        parkingAmount,
        tollAmount,
        carRental,
        airfare,
        rideshare,
        repairsMaintenance,
    ] = watch([
        `${formKey}.date_of_travel`,
        `${formKey}.miles_driven`,
        `${formKey}.parking_amount`,
        `${formKey}.toll_amount`,
        `${formKey}.car_rental`,
        `${formKey}.airfare`,
        `${formKey}.rideshare`,
        `${formKey}.repairs_maintenance`,
    ]);
    const destination = watch(`${formKey}.destination`);

    // Resolve rates for the travel date: per-diem still comes from the year
    // bucket, but mileage is picked by effective date so a mid-year IRS change
    // doesn't re-rate travel from earlier in the same year.
    const currentRates = useMemo(() => {
        if (!dateOfTravel) {
            return ratesForTravelDate(annualRates, null);
        }

        const date = new Date(dateOfTravel);
        if (isNaN(date.getTime())) {
            return ratesForTravelDate(annualRates, null);
        }

        const year = date.getFullYear();
        const yearRates = annualRates?.rates_by_year?.[year] || annualRates;
        return ratesForTravelDate(annualRates, dateOfTravel, yearRates);
    }, [dateOfTravel, annualRates]);

    // Load centralized travel schema
    useEffect(() => {
        (async () => {
            try {
                const schema = await fetchTravelSchema(apiUrl, authHeaders());
                setTravelSchema(schema);
            } catch (e) {
                console.error('Failed to load travel schema', e);
            }
        })();
    }, []);

    useEffect(() => {
        if (!formKey) return;
        const current = {
            miles_driven: parseFloat(milesDriven) || 0,
            parking_amount: parseFloat(parkingAmount) || 0,
            toll_amount: parseFloat(tollAmount) || 0,
            car_rental: parseFloat(carRental) || 0,
            airfare: parseFloat(airfare) || 0,
            rideshare: parseFloat(rideshare) || 0,
            repairs_maintenance: parseFloat(repairsMaintenance) || 0,
        } as any;
        const total = calculateTravelTotal(
            current,
            currentRates,
            travelSchema as any,
        );
        setTotalAmount(total || 0);
        setValue(`${formKey}.amount`, total || 0, {
            shouldDirty: true,
            shouldValidate: true,
        } as any);
    }, [
        milesDriven,
        parkingAmount,
        tollAmount,
        carRental,
        airfare,
        rideshare,
        repairsMaintenance,
        currentRates,
        formKey,
        travelSchema,
        setValue,
    ]);

    const currentItem = formKey ? watch(formKey) : null;

    return (
        <div className='flex gap-4 divide-x divide-bodydark'>
            <div className='w-full'>
                <div className='mt-3 w-[100%]'>
                    <label
                        htmlFor='mid-primary-medical-reason'
                        className='block font-bold'
                    >
                        Select Category
                    </label>
                    <select
                        value={FundRequestExpenseTypes.TRAVEL}
                        disabled
                        id='mid-primary-medical-reason'
                        className={`${inputStyles} bg-grey w-full`}
                    >
                        <option value='' disabled>
                            Category
                        </option>
                        {fundRequestExpenseTypesList.map((t, i) => {
                            return (
                                <option value={t} key={i}>
                                    {t}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <div className='mb-3 flex gap-4 pt-[10px]'>
                    <div className='w-1/2'>
                        <label
                            htmlFor={`${formKey}.date_of_travel`}
                            className='font-semibold'
                        >
                            Date of Travel <span className='text-red'>*</span>
                        </label>
                        <input
                            type='date'
                            className={`${inputStyles} w-full`}
                            {...register(`${formKey}.date_of_travel`, {
                                required: true,
                            })}
                        />
                    </div>
                    <div className='w-1/2'>
                        <label
                            htmlFor={`${formKey}.date_of_travel_end`}
                            className='font-semibold'
                        >
                            End Date (optional)
                        </label>
                        <input
                            type='date'
                            className={`${inputStyles} w-full`}
                            {...register(`${formKey}.date_of_travel_end`)}
                        />
                    </div>
                </div>
                <div className='mb-3 inline-block w-full pr-2 align-top md:w-1/2'>
                    <label htmlFor='total' className='font-semibold'>
                        Destination <span className='text-red'>*</span>
                    </label>
                    <input
                        className={`w-[100%] rounded-md border-2 ${
                            destination ? 'border-black' : 'border-gray-300'
                        } p-[10px]`}
                        maxLength={60}
                        {...register(`${formKey}.destination`, {
                            maxLength: {
                                value: 60,
                                message:
                                    'Destination must be 60 characters or less',
                            },
                            pattern: {
                                value: /^[^"]*$/,
                                message:
                                    'Destination cannot contain double quote characters (")',
                            },
                        })}
                    />
                    <div className='mt-1 text-sm text-gray-500'>
                        {destination?.length || 0}/60 characters
                    </div>
                    {errors[formKey]?.destination && (
                        <span className='text-red-500 mt-1 block text-sm'>
                            {(errors[formKey]?.destination as any)?.message}
                        </span>
                    )}
                </div>

                <div className='mb-3 inline-block w-full pl-2 align-top md:w-1/2'>
                    <label htmlFor='total' className='block font-semibold'>
                        Miles Driven (Round Trip)
                    </label>
                    <input
                        type='number'
                        step='0.01'
                        min='0'
                        placeholder='0.00'
                        className={`w-45 rounded-md border-2 ${
                            milesDriven && milesDriven > 0
                                ? 'border-black'
                                : 'border-gray-300'
                        } p-[10px]`}
                        {...register(`${formKey}.miles_driven`, {
                            setValueAs: (value) =>
                                value === '' ? 0 : parseFloat(value) || 0,
                        })}
                    />
                    <div className='mt-1 text-sm text-gray-500'>
                        <strong>Note:</strong> Total Travel Cost reflects{' '}
                        {currentRates?.year || 'current'} IRS medical mileage
                        rate of $
                        {parseFloat(currentRates?.medical_mileage || 0).toFixed(
                            3,
                        )}{' '}
                        per mile.
                    </div>
                </div>

                {/* Amount inputs in two-column layout */}
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    <DollarInput
                        name={`${formKey}.parking_amount`}
                        label='Parking Amount'
                        control={control}
                        defaultValue={
                            getValues(`${formKey}.parking_amount`) ?? ''
                        }
                        setValue={setValue}
                    />

                    <DollarInput
                        name={`${formKey}.toll_amount`}
                        label='Toll Amount'
                        control={control}
                        defaultValue={getValues(`${formKey}.toll_amount`) ?? ''}
                        setValue={setValue}
                    />

                    <DollarInput
                        name={`${formKey}.car_rental`}
                        label='Car Rental'
                        control={control}
                        defaultValue={getValues(`${formKey}.car_rental`) ?? ''}
                        setValue={setValue}
                    />

                    <DollarInput
                        name={`${formKey}.airfare`}
                        label='Airfare'
                        control={control}
                        defaultValue={getValues(`${formKey}.airfare`) ?? ''}
                        setValue={setValue}
                    />

                    <DollarInput
                        name={`${formKey}.rideshare`}
                        label='Rideshare (Uber, Lyft, etc.)'
                        control={control}
                        defaultValue={getValues(`${formKey}.rideshare`) ?? ''}
                        setValue={setValue}
                    />

                    <DollarInput
                        name={`${formKey}.repairs_maintenance`}
                        label='Repairs/Maintenance'
                        control={control}
                        defaultValue={
                            getValues(`${formKey}.repairs_maintenance`) ?? ''
                        }
                        setValue={setValue}
                    />
                </div>

                <div className='mb-2'>
                    <label className='mb-2 block font-bold'>
                        Total Travel Cost
                    </label>
                    <strong>{toUSD(totalAmount)}</strong>
                </div>
                <UploadDocument
                    expenseItem={currentItem}
                    setSlideIndex={onDocumentClick}
                    selectedIndex={selectedDocumentIndex}
                />
                <div className='mt-5'>
                    <label htmlFor='total' className='block font-semibold'>
                        GL Code
                    </label>
                    <Controller
                        name={`${formKey}.gl_code`}
                        control={control}
                        render={({ field }) => (
                            <Select
                                menuPlacement='auto'
                                {...field}
                                value={glCodes.filter(
                                    (option: SelectOption) =>
                                        option.value ===
                                        getValues(`${formKey}.gl_code`),
                                )}
                                onChange={(option) => {
                                    field.onChange(option?.value);
                                }}
                                options={glCodes}
                                formatOptionLabel={(option: SelectOption) =>
                                    `${option.value} - ${option.label}`
                                }
                            />
                        )}
                    />
                </div>
            </div>
        </div>
    );
};

export default TravelExpenseForm;
